import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useRouterState, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { MessageCircle } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { UserMenu } from "@/components/user-menu";
import { MobileTopBar } from "@/components/mobile-top-bar";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { Button } from "@/components/ui/button";
import { canAccess, useUserRole, type AppRole } from "@/hooks/use-user-role";
import { setCachedStateValue } from "@/hooks/use-cached-state";
import { supabase } from "@/integrations/supabase/client";
import { useUnreadChats } from "@/hooks/use-unread-chats";
import { callBus } from "@/components/chat/call-bus";
import { cn } from "@/lib/utils";

// Track which routes have already been visited OR preloaded in this session
// so we only show the skeleton the very first time a page needs to fetch.
// Revisiting or opening a preloaded menu feels instant, like marketku.id.
const visitedRoutes = new Set<string>();

// Full list of authenticated routes to warm in the background as soon as the
// shell mounts. Order = priority (most-used first).
const ALL_AUTH_ROUTES = [
  "/dashboard",
  "/students",
  "/teachers",
  "/members",
  "/attendance",
  "/schedules",
  "/announcements",
  "/staff-announcements",
  "/gallery",
  "/payment-types",
  "/student-bills",
  "/payments",
  "/payment-settings",
  "/store",
  "/store/products",
  "/store/categories",
  "/store/banners",
  "/store/settings",
  "/school",
  "/app-updates",
  "/profile",
] as const;

type CachedAnnouncement = {
  id: string;
  title: string;
  content: string;
  category: string;
  date: string;
  image_url: string | null;
  pdf_url: string | null;
};

type CachedStudent = {
  id: string;
  nisn: string | null;
  full_name: string;
  class_id: string | null;
  gender: "L" | "P" | null;
  birth_date: string | null;
  address: string | null;
  parent_name: string | null;
  father_name: string | null;
  mother_name: string | null;
  parent_phone: string | null;
  email: string | null;
  photo_url: string | null;
};

type CachedClass = { id: string; name: string };

async function warmInstantPageData(role: AppRole) {
  const jobs: PromiseLike<unknown>[] = [];

  if (canAccess(role, "/students")) {
    jobs.push(
      supabase.from("students").select("*").order("full_name").then(({ data, error }) => {
        if (!error && data) setCachedStateValue("students:rows", data as CachedStudent[]);
      }),
      supabase.from("classes").select("id,name").order("name").then(({ data, error }) => {
        if (!error && data) setCachedStateValue("students:classes", data as CachedClass[]);
      })
    );
  }

  if (canAccess(role, "/announcements")) {
    jobs.push(
      supabase
        .from("announcements")
        .select("*")
        .order("date", { ascending: false })
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
          if (!error && data) setCachedStateValue("announcements:rows", data as CachedAnnouncement[]);
        })
    );
  }

  if (canAccess(role, "/staff-announcements")) {
    jobs.push(
      supabase
        .from("staff_announcements")
        .select("*")
        .order("date", { ascending: false })
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
          if (!error && data) setCachedStateValue("staff-announcements:rows", data as CachedAnnouncement[]);
        })
    );
  }

  await Promise.allSettled(jobs);
}

// Shell stays stable on navigation — no full-page skeleton flash. Each page
// shows its own small inline loader only where data is still loading, so
// menu/header never rearranges when a tab is tapped (like marketku.id).
function MobileRouteSkeleton({ children }: { children: ReactNode; refreshKey: number }) {
  return <>{children}</>;
}





function RoleGuard({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const navigate = useNavigate();
  const { role, loading } = useUserRole();

  useEffect(() => {
    if (loading || !role) return;
    if (!canAccess(role, pathname)) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [role, loading, pathname, navigate]);

  if (loading) return <>{children}</>;
  if (role && !canAccess(role, pathname)) return null;
  return <>{children}</>;
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const router = useRouter();
  const { role } = useUserRole();
  const [refreshKey, setRefreshKey] = useState(0);
  const isChat = pathname === "/chat" || pathname.startsWith("/chat/");
  const { total: chatUnread } = useUnreadChats();
  const isProfile = pathname === "/profile" || pathname.startsWith("/profile/");
  const hideMobileTopBar = isChat || isProfile;
  const [callActive, setCallActive] = useState(() => callBus.isActive());
  const [callMinimized, setCallMinimized] = useState(() => callBus.isMinimized());
  useEffect(() => callBus.subscribeActive(setCallActive), []);
  useEffect(() => callBus.subscribeMinimized(setCallMinimized), []);
  const hideBottomNav = isChat && callActive && !callMinimized;

  useEffect(() => {
    let lastRefresh = 0;
    const refreshRoute = () => {
      const now = Date.now();
      if (now - lastRefresh < 800) return;
      lastRefresh = now;
      setRefreshKey((key) => key + 1);
    };
    window.addEventListener("almunajah:data-refresh", refreshRoute);
    return () => window.removeEventListener("almunajah:data-refresh", refreshRoute);
  }, []);

  // Warm every allowed route in the background so tapping any menu feels
  // instant — module + loader data are already cached before the click.
  useEffect(() => {
    if (!role) return;
    const idle = (cb: () => void) =>
      typeof (window as any).requestIdleCallback === "function"
        ? (window as any).requestIdleCallback(cb, { timeout: 2000 })
        : window.setTimeout(cb, 200);

    idle(() => {
      const targets = ALL_AUTH_ROUTES.filter((r) => canAccess(role as AppRole, r));
      targets.forEach((to, i) => {
        window.setTimeout(() => {
          router
            .preloadRoute({ to })
            .then(() => { visitedRoutes.add(to); })
            .catch(() => { /* ignore preload failures */ });
        }, i * 60);
      });
      warmInstantPageData(role as AppRole).catch(() => { /* keep navigation smooth even if warming fails */ });
    });
  }, [role, router]);

  // App-wide presence: user counts as "online" as long as any Al-Munajah page
  // is open (not just the chat page). Marks offline on tab close / logout.
  useEffect(() => {
    let cancelled = false;
    let iv: number | null = null;
    let userId: string | null = null;
    let accessToken: string | null = null;

    const beat = () => {
      if (!userId) return;
      supabase.rpc("touch_last_seen").then(() => {});
    };

    const rememberSessionToken = async () => {
      const { data } = await supabase.auth.getSession();
      accessToken = data.session?.access_token ?? null;
    };

    const start = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      userId = user.id;
      await rememberSessionToken();
      beat();
      iv = window.setInterval(beat, 20000);
    };

    const onVis = () => {
      if (document.visibilityState === "visible") {
        rememberSessionToken().then(beat);
      } else {
        markOffline(true);
      }
    };
    const onActive = () => beat();
    const markOffline = (keepalive = false) => {
      if (!userId) return;
      if (keepalive && accessToken) {
        try {
          const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/mark_offline`;
          const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: key,
              Authorization: `Bearer ${accessToken}`,
            },
            body: "{}",
            keepalive: true,
          }).catch(() => {});
        } catch { /* ignore */ }
      }
      supabase.rpc("mark_offline").then(() => {});
    };
    const onPageHide = () => {
      if (!userId) return;
      // Keepalive fetch uses the already-remembered token; no async session lookup
      // during page close, so the offline mark is sent immediately.
      markOffline(true);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onActive);
    window.addEventListener("pageshow", onActive);
    window.addEventListener("online", onActive);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        markOffline(true);
        if (iv) { clearInterval(iv); iv = null; }
        userId = null;
        accessToken = null;
      } else if (event === "SIGNED_IN") {
        const nextUserId = session?.user?.id ?? null;
        if (nextUserId && nextUserId !== userId) {
          if (userId) markOffline(true);
          if (iv) { clearInterval(iv); iv = null; }
          userId = null;
          accessToken = session?.access_token ?? null;
          void start();
        } else {
          accessToken = session?.access_token ?? accessToken;
        }
      } else {
        accessToken = session?.access_token ?? accessToken;
      }
    });

    start();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onActive);
      window.removeEventListener("pageshow", onActive);
      window.removeEventListener("online", onActive);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      if (iv) clearInterval(iv);
      sub?.subscription?.unsubscribe();
    };
  }, []);






  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <div className="hidden md:block">
          <AppSidebar />
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          <header className="hidden md:flex h-14 items-center gap-3 border-b bg-surface/80 backdrop-blur px-3 sticky top-0 z-30">
            <SidebarTrigger />
            <div className="flex-1" />
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label="Smart Chat Al-Munajah"
              className={`relative h-10 w-10 rounded-full hover:bg-primary/10 ${isChat ? "bg-primary/10 text-primary" : ""}`}
            >
              <Link to="/chat">
                <MessageCircle className="h-5 w-5" />
                {chatUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none">
                    {chatUnread > 99 ? "99+" : chatUnread}
                  </span>
                )}
              </Link>
            </Button>
            <UserMenu />
          </header>
          {!hideMobileTopBar && <MobileTopBar />}
          <main
            className={isChat
              ? cn(
                  "fixed inset-x-0 top-0 w-full min-w-0 overflow-hidden overscroll-none md:static md:flex-1 md:overflow-visible md:pb-0",
                  hideBottomNav ? "bottom-0" : "bottom-[calc(4rem+env(safe-area-inset-bottom))]",
                )
              : "flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto pb-24 md:pb-8"}
          >
            <RoleGuard><MobileRouteSkeleton refreshKey={refreshKey}>{children}</MobileRouteSkeleton></RoleGuard>
          </main>
          {!hideBottomNav && <MobileBottomNav />}
        </div>
      </div>
    </SidebarProvider>
  );
}
